import { useMutation, useQuery } from '@tanstack/react-query';

import objectivePeriodService from './objectivePeriodService';

import type { ObjectivePeriodStatusType } from '@/constants/ObjectivePeriodStatus';
import type { ObjectivePeriod, ObjectivePeriodRequest } from '@/types/objective-periods';

import { invalidatePillarSummaries } from '@/api/objectives';
import { queryClient } from '@/AppWithProviders';
import {
    ObjectivePeriodMutationKey,
    ObjectivePeriodQueryKey,
    ObjectiveQueryKey,
} from '@/constants/QueryKeys';

type UpdateObjectivePeriodParams = { id: string; request: ObjectivePeriodRequest };
type SetObjectivePeriodStatusParams = { id: string; status: ObjectivePeriodStatusType };

/**
 * A period owns the dates and the attachment of the objectives that point to it: saving one
 * resynchronizes their window server-side, and deleting one detaches them. Every mutation
 * therefore refreshes the objective list and every pillar summary alongside the period list.
 */
const invalidateObjectivePeriods = () => {
    queryClient.invalidateQueries({ queryKey: [ObjectivePeriodQueryKey.OBJECTIVE_PERIODS] });
    queryClient.invalidateQueries({ queryKey: [ObjectiveQueryKey.OBJECTIVES] });
    invalidatePillarSummaries();
};

const invalidateObjectivePeriodsAndDetail = () => {
    invalidateObjectivePeriods();
    queryClient.invalidateQueries({ queryKey: [ObjectivePeriodQueryKey.OBJECTIVE_PERIOD] });
};

export const useGetObjectivePeriods = (status?: ObjectivePeriodStatusType) => {
    return useQuery<ObjectivePeriod[]>({
        queryKey: [ObjectivePeriodQueryKey.OBJECTIVE_PERIODS, status],
        queryFn: () => objectivePeriodService.getObjectivePeriods(status),
    });
};

export const useGetObjectivePeriodById = (id: string) => {
    return useQuery<ObjectivePeriod>({
        queryKey: [ObjectivePeriodQueryKey.OBJECTIVE_PERIOD, id],
        queryFn: () => objectivePeriodService.getObjectivePeriodById(id),
        enabled: !!id,
    });
};

export const useCreateObjectivePeriod = () => {
    return useMutation<ObjectivePeriod, Error, ObjectivePeriodRequest>({
        mutationKey: [ObjectivePeriodMutationKey.CREATE_OBJECTIVE_PERIOD],
        mutationFn: (request) => objectivePeriodService.createObjectivePeriod(request),
        onSuccess: invalidateObjectivePeriods,
    });
};

export const useUpdateObjectivePeriod = () => {
    return useMutation<ObjectivePeriod, Error, UpdateObjectivePeriodParams>({
        mutationKey: [ObjectivePeriodMutationKey.UPDATE_OBJECTIVE_PERIOD],
        mutationFn: ({ id, request }) => objectivePeriodService.updateObjectivePeriod(id, request),
        onSuccess: invalidateObjectivePeriodsAndDetail,
    });
};

export const useSetObjectivePeriodStatus = () => {
    return useMutation<ObjectivePeriod, Error, SetObjectivePeriodStatusParams>({
        mutationKey: [ObjectivePeriodMutationKey.SET_OBJECTIVE_PERIOD_STATUS],
        mutationFn: ({ id, status }) => objectivePeriodService.setObjectivePeriodStatus(id, status),
        onSuccess: invalidateObjectivePeriodsAndDetail,
    });
};

export const useDeleteObjectivePeriod = () => {
    return useMutation<ObjectivePeriod, Error, string>({
        mutationKey: [ObjectivePeriodMutationKey.DELETE_OBJECTIVE_PERIOD],
        mutationFn: (id) => objectivePeriodService.deleteObjectivePeriod(id),
        onSuccess: invalidateObjectivePeriods,
    });
};
